import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { FormattedMessage, useIntl } from 'react-intl';
import axiosConfig from 'src/Resources/axiosConfig';
import { UserContext } from 'src/contexts/userContext';
import { UserType } from 'src/interfaces/UserTypes';
import { TicketStatus } from 'src/interfaces/TicketTypes';
import StatusChip from './StatusChip';
import CreateTicketModal from './CreateTicketModal';
import PartnerCreateTicketModal from './PartnerCreateTicketModal';

interface Props {
  installationId: number;
}

// Common shape across admin (Ticket) and partner (PartnerTicketSummary) rows.
// priority/installationId are present only on the admin/partner shapes resp.
type TicketRow = {
  id: number;
  subject: string;
  status: number;
  priority?: number;
  installationId?: number | null;
  createdAt: string;
};

const statusCountKeys: {
  status: number;
  id: string;
  defaultMessage: string;
  color: string;
}[] = [
  { status: TicketStatus.Open, id: 'statusOpen', defaultMessage: 'Open', color: '#d32f2f' },
  { status: TicketStatus.InProgress, id: 'statusInProgress', defaultMessage: 'In Progress', color: '#ed6c02' },
  { status: TicketStatus.Resolved, id: 'statusResolved', defaultMessage: 'Solved', color: '#2e7d32' }
];

function InstallationTicketsTab({ installationId }: Props) {
  const navigate = useNavigate();
  const intl = useIntl();
  const userCtx = useContext(UserContext);
  const isPartner = userCtx?.currentUser?.userType === UserType.partner;

  const [tickets, setTickets] = useState<TicketRow[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  const loadFailed = () =>
    setError(
      intl.formatMessage({
        id: 'failedToLoadTickets',
        defaultMessage: 'Failed to load tickets.'
      })
    );

  const fetchTickets = () => {
    setLoading(true);
    // Partners see only their own tickets for this installation (own-created,
    // scoped client-side from GetMyTickets); admins see all installation tickets.
    if (isPartner) {
      axiosConfig
        .get('/GetMyTickets')
        .then((res) => {
          setTickets(
            (res.data as TicketRow[]).filter(
              (t) => t.installationId === installationId
            )
          );
          setError('');
        })
        .catch(loadFailed)
        .finally(() => setLoading(false));
    } else {
      axiosConfig
        .get('/GetTicketsForInstallation', { params: { installationId } })
        .then((res) => {
          setTickets(res.data);
          setError('');
        })
        .catch(loadFailed)
        .finally(() => setLoading(false));
    }
  };

  useEffect(() => {
    fetchTickets();
    // eslint-disable-next-line
  }, [installationId, isPartner]);

  const statusCounts = statusCountKeys.map((s) => ({
    ...s,
    count: tickets.filter((t) => t.status === s.status).length
  }));

  if (loading) return null;

  const onCreated = () => {
    setCreateOpen(false);
    fetchTickets();
  };

  return (
    <Box sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">
          <FormattedMessage id="tickets" defaultMessage="Tickets" />
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setCreateOpen(true)}
        >
          <FormattedMessage id="createTicket" defaultMessage="Create Ticket" />
        </Button>
      </Box>

      <Box display="flex" gap={1.5} mb={2} flexWrap="wrap">
        {statusCounts.map((s) => (
          <Chip
            key={s.status}
            label={`${intl.formatMessage({ id: s.id, defaultMessage: s.defaultMessage })}: ${s.count}`}
            size="small"
            sx={{
              backgroundColor: s.count > 0 ? s.color : '#e0e0e0',
              color: s.count > 0 ? '#fff' : '#757575',
              fontWeight: 600
            }}
          />
        ))}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {tickets.length === 0 && !error ? (
        <Typography variant="body2" color="text.secondary">
          <FormattedMessage id="noTickets" defaultMessage="No tickets found." />
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>
                  <FormattedMessage id="subject" defaultMessage="Subject" />
                </TableCell>
                <TableCell>
                  <FormattedMessage id="status" defaultMessage="Status" />
                </TableCell>
                {!isPartner && (
                  <TableCell>
                    <FormattedMessage id="priority" defaultMessage="Priority" />
                  </TableCell>
                )}
                <TableCell>
                  <FormattedMessage id="createdAt" defaultMessage="Created" />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {[...tickets]
                .sort(
                  (a, b) =>
                    new Date(b.createdAt).getTime() -
                    new Date(a.createdAt).getTime()
                )
                .map((ticket) => (
                  <TableRow
                    key={ticket.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/tickets/${ticket.id}`)}
                  >
                    <TableCell>{ticket.id}</TableCell>
                    <TableCell>{ticket.subject}</TableCell>
                    <TableCell>
                      <StatusChip status={ticket.status} />
                    </TableCell>
                    {!isPartner && (
                      <TableCell>
                        {intl.formatMessage({
                          id: `priority${['Critical', 'High', 'Medium', 'Low'][ticket.priority ?? 0]}`,
                          defaultMessage: ['Critical', 'High', 'Medium', 'Low'][
                            ticket.priority ?? 0
                          ]
                        })}
                      </TableCell>
                    )}
                    <TableCell>
                      {new Date(ticket.createdAt).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {isPartner ? (
        <PartnerCreateTicketModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={onCreated}
          defaultInstallationId={installationId}
        />
      ) : (
        <CreateTicketModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={onCreated}
          defaultInstallationId={installationId}
        />
      )}
    </Box>
  );
}

export default InstallationTicketsTab;
